// src/components/standard/ListView.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { StandardItem } from '../../types/standard';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';
import { resolveColor } from '../../utils/colorUtils';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';
import { WalkIcon, BikeIcon, GiftIcon } from '../icons';
import { getDisplayTravelType } from '../../utils/timeUtils';
import { formatTravelTime } from '../../utils/formatDuration';

interface ListViewProps {
  items: StandardItem[];
  onItemClick: (item: StandardItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
}

export const ListView: React.FC<ListViewProps> = ({
  items,
  onItemClick,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'standard'
  });

  const backgroundColor = resolveColor(config['sectionBack-color'], config, '#f9fafb');

  // Créer un composant séparé pour chaque item
  const ListItemCard = React.memo(({ event }: { event: StandardItem }) => {
    // Get image URL from image_metadata.url if available
    const imageUrl = event.image_metadata?.url || event.image;
    const { displayImage, shouldDisplay } = useImageDisplay(imageUrl, 'listCard');

    const handleItemClick = () => {
      analytics.trackItemAction(event.id, 'item_click', 'standard', {
        item_name: event.name,
        category: event.category,
        type: event.type,
        source: 'list_card'
      });
      onItemClick(event);
    };

    const handleCategoryClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.trackFilter('category_filter', event.category);
      onCategorySelect(event.category);
    };

    const handleTypeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.trackFilter('type_filter', event.type);
      onTypeSelect(event.type);
    };

    // Determine which travel time to display based on limit_walk
    const renderTravelTime = () => {
      // Get limit_walk from config, default to 25 if not set
      const limitWalk = parseInt(config.limit_walk || '25', 10);
      
      // Determine which travel type to display
      const travelType = getDisplayTravelType(
        event.timewalk, 
        event.timebike, 
        event.timedrive, 
        limitWalk
      );
      
      // Return the appropriate component based on travel type
      switch (travelType) {
        case 'walk':
          return (
            <div className="flex items-center">
              <WalkIcon size={16} className="mr-1" />
              <span>{formatTravelTime(event.timewalk, i18n.language)}</span>
            </div>
          );
        case 'bike':
          return (
            <div className="flex items-center">
              <BikeIcon size={16} className="mr-1" />
              <span>{formatTravelTime(event.timebike, i18n.language)}</span>
            </div>
          );
        case 'drive':
          return (
            <div className="flex items-center">
              <Car size={16} className="mr-1" fill="currentColor" />
              <span>{formatTravelTime(event.timedrive, i18n.language)}</span>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 relative"
        style={{ borderRadius: config.border_radius }}
      >
        {/* Gift icon for items with offers */}
        {event.offer && (
          <div 
            className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center z-10"
            style={{ 
              backgroundColor: config['major-color'],
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius
            }}
          >
            <GiftIcon size={20} className="text-white" />
          </div>
        )}
        
        <div 
          className="cursor-pointer"
          onClick={handleItemClick}
        >
          {shouldDisplay && displayImage && (
            <div className="relative pb-[66.666%]">
              <img
                src={displayImage}
                alt={event.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-4">
            <h3 className="text-lg font-medium mb-2">{event.name}</h3>
            
            {/* Ligne 1: Adresse complète */}
            {event.fulladress && (
              <p className="text-sm text-gray-600 mb-1">
                {event.fulladress}
              </p>
            )}

            {/* Ligne 2: Distance + temps de trajet + prix */}
            <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
              {/* Distance */}
              {event.distance && (
                <div className="flex items-center text-gray-600">
                  <MapPin size={16} className="mr-1" />
                  <span>{event.distance}km</span>
                </div>
              )}
              
              {/* Temps de trajet (walk, bike ou drive selon la distance) */}
              {renderTravelTime()}
              
              {/* Prix */}
              {event.price && (
                <div 
                  className="flex items-center"
                  style={{ color: config['major-color'] }}
                >
                  <Tag size={16} className="mr-1" />
                  <span>{event.price}</span>
                </div>
              )}
              
              {/* Ouverture lundi */}
              {event.ouverture_lundi === 'oui' && (
                <div className="flex items-center text-gray-600">
                  <Clock size={16} className="mr-1" />
                  <span>{getTranslatedText(translations, 'events_monday_open', i18n.language)}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={handleCategoryClick}
                className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                style={{ backgroundColor: categoryColors.get(event.category) || config['medium-color'] }}
              >
                {getTranslatedCategory(translations, event.category, i18n.language)}
              </button>
              
              {event.type && (
                <button
                  type="button"
                  onClick={handleTypeClick}
                  className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white opacity-70"
                  style={{ backgroundColor: config['medium-color'] }}
                >
                  {getTranslatedCategory(translations, event.type, i18n.language)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (!items || items.length === 0) {
    return (
      <div 
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor }}
      >
        <p className="text-gray-500">Aucun élément disponible</p>
      </div>
    );
  }

  return (
    <div 
      className="w-full min-h-screen pb-20"
      style={{ backgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((event) => (
            <ListItemCard key={event.id} event={event} />
          ))}
        </div>
      </div>
    </div>
  );
};